import React from "react";

/** Convert MUI/d3 pie mid-angle (radians) to cartesian coords. */
const polarToCartesian = (radius, startAngle, endAngle) => {
  const mid = (startAngle + endAngle) / 2;
  return {
    x: Math.sin(mid) * radius,
    y: -Math.cos(mid) * radius,
  };
};

/**
 * Pie arc label rendered outside the slice, with a short connector line.
 * Pass `outerRadius` via slotProps.pieArcLabel so the line starts at the arc edge.
 */
const PieArcLabelWithConnector = React.forwardRef(
  function PieArcLabelWithConnector(props, ref) {
    const {
      startAngle,
      endAngle,
      arcLabelRadius,
      formattedArcLabel,
      color,
      outerRadius,
      hidden,
      isFaded,
      className,
    } = props;

    if (!formattedArcLabel || hidden) {
      return null;
    }

    const edgeRadius =
      typeof outerRadius === "number" && outerRadius > 0
        ? outerRadius
        : Math.max(0, arcLabelRadius - 18);
    const elbowRadius = edgeRadius + (arcLabelRadius - edgeRadius) * 0.45;

    const edge = polarToCartesian(edgeRadius, startAngle, endAngle);
    const elbow = polarToCartesian(elbowRadius, startAngle, endAngle);
    const label = polarToCartesian(arcLabelRadius, startAngle, endAngle);

    return (
      <g
        ref={ref}
        className={className}
        opacity={isFaded ? 0.35 : 1}
        style={{ pointerEvents: "none" }}
      >
        <polyline
          points={`${edge.x},${edge.y} ${elbow.x},${elbow.y} ${label.x},${label.y}`}
          fill="none"
          stroke={color || "#757575"}
          strokeWidth={1.25}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx={edge.x} cy={edge.y} r={2} fill={color || "#757575"} />
        <text
          x={label.x}
          y={label.y}
          textAnchor="middle"
          dominantBaseline="middle"
          style={{
            fontSize: 12,
            fontWeight: 600,
            fill: "currentColor",
            paintOrder: "stroke",
            stroke: "#fff",
            strokeWidth: 3,
          }}
        >
          {formattedArcLabel}
        </text>
      </g>
    );
  }
);

export const getOutsidePieSeriesProps = (outerRadius, showLabels) => {
  if (!showLabels) {
    return {
      arcLabel: undefined,
      arcLabelRadius: undefined,
    };
  }

  const labelRadius = outerRadius + 28;
  return {
    arcLabel: "value",
    arcLabelMinAngle: 0,
    arcLabelRadius: labelRadius,
  };
};

export const getOutsidePieSlots = (outerRadius) => ({
  pieArcLabel: PieArcLabelWithConnector,
});

export const getOutsidePieSlotProps = (outerRadius) => ({
  pieArcLabel: { outerRadius },
  legend: {
    direction: "row",
    position: { vertical: "bottom", horizontal: "middle" },
  },
});

export default PieArcLabelWithConnector;
